import React, { useState, ChangeEvent, useEffect } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import {
  Button,
  Select,
  MenuItem,
  TextField,
  FormControl,
  Box,
} from "@mui/material";
import { SelectChangeEvent } from "@mui/material";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import dayjs, { Dayjs } from "dayjs";

import { LOYALTY_TYPE } from "../../constants/enums";
import {
  useCreateLoyaltyPointMutation,
  useUpdateLoyaltyPointMutation,
  LoyaltyPointItem
} from "../../store/api/loyaltyAPI";
import { showToast, showError } from "../../constants/toasts";

const AddPointRule = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const location = useLocation();
  const ruleFromState = location.state?.rule as LoyaltyPointItem | undefined;

  const isEditMode = Boolean(id);

  const [createLoyaltyPoint, { isLoading: isCreating }] = useCreateLoyaltyPointMutation();
  const [updateLoyaltyPoint, { isLoading: isUpdating }] = useUpdateLoyaltyPointMutation();

  const isLoading = isCreating || isUpdating;

  const [ruleName, setRuleName] = useState("");
  const [actionTrigger, setActionTrigger] = useState<number | "">("");
  const [pointValue, setPointValue] = useState("");
  const [minSpend, setMinSpend] = useState("");
  const [startDate, setStartDate] = useState<Dayjs | null>(dayjs());
  const [endDate, setEndDate] = useState<Dayjs | null>(dayjs().add(1, "month"));

  // Populate form from navigation state
  useEffect(() => {
    if (isEditMode && ruleFromState) {
      setRuleName(ruleFromState.name);
      setActionTrigger(ruleFromState.type);
      setPointValue(String(ruleFromState.points));
      setMinSpend(ruleFromState.minimumSpend ? String(ruleFromState.minimumSpend) : "");
      setStartDate(dayjs(ruleFromState.startDate));
      setEndDate(dayjs(ruleFromState.endDate));
    }
  }, [isEditMode, ruleFromState]);

  const handleSave = async () => {
    if (!ruleName) {
      showError("Please enter rule name");
      return;
    }
    if (!actionTrigger) {
      showError("Please select an action trigger");
      return;
    }
    if (!pointValue) {
      showError("Please enter point value");
      return;
    }
    if (!startDate || !endDate) {
      showError("Please select activation period");
      return;
    }

    const payload = {
      name: ruleName,
      type: Number(actionTrigger),
      points: Number(pointValue),
      minimumSpend: minSpend ? Number(minSpend) : null,
      startDate: startDate!.toISOString(),
      endDate: endDate!.toISOString(),
    };

    try {
      let res;
      if (isEditMode && id) {
        res = await updateLoyaltyPoint({ id, body: payload }).unwrap();
        showToast(res.message || "Rule updated successfully");
      } else {
        res = await createLoyaltyPoint(payload).unwrap();
        showToast(res.message || "Rule created successfully");
      }
      navigate("/manage-loyality-points");
    } catch (error: any) {
      console.error("Error saving rule:", error);
      showError(error?.data?.message || `Failed to ${isEditMode ? 'update' : 'create'} rule`);
    }
  };

  const handleChangeName = (e: ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value;
    if (val.length <= 40) {
      setRuleName(val);
    }
  };

  const handleChangePoints = (e: ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value;
    // Allow only numbers and limit to 10 digits
    if (/^\d{0,10}$/.test(val)) {
      setPointValue(val);
    }
  };

  const handleChangeMinSpend = (e: ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value;
    // Allow only numbers and limit to 10 digits
    if (/^\d{0,10}$/.test(val)) {
      setMinSpend(val);
    }
  };


  return (
    <>
      <div className="main_title">
        <h1>{isEditMode ? "Edit Point-Earning Rule" : "Add Point-Earning Rule"}</h1>
        <p>
          <button onClick={() => navigate("/dashboard")}>Dashboard</button> -{" "}
          <button onClick={() => navigate("/manage-loyality-points")}>
            Loyalty Points Management
          </button>{" "}
          - {isEditMode ? "Edit Rule" : "Add Rule"}
        </p>
      </div>

      <div className="cards">
        <div className="form gap_p">
          {/* Rule Name */}
          <div className="control_group w_33">
            <label>Rule Name</label>
            <TextField
              fullWidth
              id="ruleName"
              placeholder="Enter rule name (e.g., Purchase Reward)"
              value={ruleName}
              onChange={handleChangeName}
            />
          </div>

          {/* Action / Trigger */}
          <div className="control_group w_33">
            <label>Action / Trigger</label>
            <FormControl fullWidth>
              <Select
                id="actionTrigger"
                value={actionTrigger as any}
                onChange={(e: SelectChangeEvent<any>) =>
                  setActionTrigger(e.target.value)
                }
                displayEmpty
              >
                <MenuItem value="" disabled>Select an action</MenuItem>
                {Object.entries(LOYALTY_TYPE).map(([key, value]) => (
                  <MenuItem key={value} value={value}>
                    {key.charAt(0) + key.slice(1).toLowerCase().replace(/_/g, ' ')}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </div>

          {/* Point Value */}
          <div className="control_group w_33">
            <label>Point Value</label>
            <TextField
              fullWidth
              id="pointValue"
              placeholder="e.g., 100"
              value={pointValue}
              onChange={handleChangePoints}
              type="text"
            />
          </div>

          {/* Min Spend / Threshold */}
          <div className="control_group w_33">
            <label>Min Spend / Threshold</label>
            <TextField
              fullWidth
              id="minSpend"
              placeholder="e.g., 500"
              value={minSpend}
              onChange={handleChangeMinSpend}
              type="text"
            />
          </div>

          {/* Activation Period */}
          <div className="control_group w_100">
            <label>Activation Period</label>
            <Box sx={{ display: "flex", gap: "1rem" }}>
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <DatePicker
                  label="Start Date"
                  value={startDate}
                  onChange={(newValue) => setStartDate(newValue)}
                  format="DD MMM YYYY"
                  minDate={dayjs()}
                />
              </LocalizationProvider>

              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <DatePicker
                  label="End Date"
                  value={endDate}
                  onChange={(newValue) => setEndDate(newValue)}
                  format="DD MMM YYYY"
                  minDate={startDate || dayjs()}
                />
              </LocalizationProvider>
            </Box>
          </div>

          {/* Save Button */}
          <div className="form_btn" style={{ marginTop: "1rem" }}>
            <Button className="btn btn_primary" onClick={handleSave} disabled={isLoading}>
              {isLoading ? (isEditMode ? "Updating..." : "Saving...") : (isEditMode ? "Update Rule" : "Save Rule")}
            </Button>
          </div>
        </div>
      </div>
    </>
  );
};

export default AddPointRule;
